// src/pages/AboutPage.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Users, Cpu, Shield, Globe, Award, Zap, Anchor } from 'lucide-react';

const AboutPage = () => {
  const { t } = useTranslation();

  const SectionHeader = ({ title, icon }) => (
    <div className="flex items-center gap-3 mb-6 border-b border-gray-200 pb-3">
      <div className="p-2 bg-indigo-100 rounded-lg text-indigo-700">
        {icon}
      </div>
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto space-y-12">
        
        {/* --- HERO SECTION --- */}
        <div className="bg-white p-10 rounded-2xl shadow-sm text-center">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-6 tracking-tight">
            {/* CHANGED 'about' to 'about_page' */}
            {t('about_page.hero.title', 'The Aulice Ecosystem')}
          </h1>
          <p className="text-xl text-indigo-600 font-medium mb-8">
            {t('about_page.hero.subtitle', 'Education as the Architect of Reality')}
          </p>
          <p className="text-lg text-gray-700 leading-relaxed max-w-3xl mx-auto">
            {t('about_page.hero.intro', 'Welcome to the Aulice Ecosystem...')}
          </p>
        </div>

        {/* --- CORE PRINCIPLES --- */}
        <div className="bg-white p-10 rounded-2xl shadow-sm">
          <SectionHeader title={t('about_page.principles.title', 'Core Principles')} icon={<Shield size={24} />} />
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="p-6 bg-indigo-50 rounded-xl border border-indigo-100">
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('about_page.principles.holistic.title', 'Holistic & Formative Education')}</h3>
              <p className="text-gray-700">
                {t('about_page.principles.holistic.text', 'What we offer is not simply training...')}
              </p>
            </div>
            <div className="p-6 bg-emerald-50 rounded-xl border border-emerald-100">
              <h3 className="text-lg font-bold text-gray-900 mb-3">{t('about_page.principles.ethics.title', 'Ethical Formation & Service')}</h3>
              <p className="text-gray-700">
                {t('about_page.principles.ethics.text', 'Character formation is essential...')}
              </p>
            </div>
          </div>
        </div>

        {/* --- THE TOOLSET --- */}
        <div className="bg-white p-10 rounded-2xl shadow-sm">
          <SectionHeader title={t('about_page.tools.title', 'The Aulice Toolset')} icon={<Cpu size={24} />} />
          
          <div className="space-y-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <BookOpen size={20} className="text-blue-600"/> 
                {t('about_page.tools.pathways.title', 'Mastery Pathways')}
              </h3>
              <p className="text-gray-700">
                {t('about_page.tools.pathways.text', 'Replacing traditional static curricula...')}
              </p>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <Users size={20} className="text-orange-600"/> 
                {t('about_page.tools.scenarios.title', 'Scenarios & Unit 1')}
              </h3>
              <p className="text-gray-700">
                {t('about_page.tools.scenarios.text', 'Our Scenarios are tools to train live communication...')}
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="p-5 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">{t('about_page.tools.argot.title', 'Argot')}</h4>
                <p className="text-sm text-gray-600">
                  {t('about_page.tools.argot.text', 'An intensive instrument to train professional terminology...')}
                </p>
              </div>
              <div className="p-5 bg-gray-50 rounded-lg">
                <h4 className="font-bold text-gray-900 mb-2">{t('about_page.tools.lexicon.title', 'The Smart Lexicon')}</h4>
                <p className="text-sm text-gray-600">
                  {t('about_page.tools.lexicon.text', 'Words can be assigned by teachers or added by students...')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* --- YOUNG GENIUS --- */}
        <div className="bg-white p-10 rounded-2xl shadow-sm border-l-8 border-yellow-400">
          <SectionHeader title={t('about_page.youngGenius.title', 'Young Genius: Restoring Willpower')} icon={<Zap size={24} />} />
          
          <p className="text-lg text-gray-800 font-semibold mb-4">
            {t('about_page.youngGenius.subtitle', 'For different ages and learning aspirations.')}
          </p>
          <p className="text-gray-700 mb-4 leading-relaxed">
            {t('about_page.youngGenius.text1', 'This program is an antidote...')}
          </p>
          <p className="text-gray-700 leading-relaxed">
            {t('about_page.youngGenius.text2', 'We provide goals in life...')}
          </p>
        </div>

        {/* --- GAMIFIED LEARNING --- */}
        <div className="bg-gray-900 text-white p-10 rounded-2xl shadow-lg">
          <div className="flex items-center gap-3 mb-8 border-b border-gray-700 pb-3">
            <div className="p-2 bg-purple-600 rounded-lg text-white">
              <Award size={24} />
            </div>
            <h2 className="text-2xl font-bold">{t('about_page.games.title', 'Immersive Learning Games')}</h2>
          </div>

          <div className="space-y-8">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-purple-400 mb-2">Autarky</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {t('about_page.games.autarky.text', 'Players undergo a journey...')}
                </p>
              </div>
            </div>

            <div className="border-t border-gray-700 my-4"></div>

            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-emerald-400 mb-2">Project Veridian</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  {t('about_page.games.veridian.text', 'The player arrives from a developed world...')}
                </p>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AboutPage;